import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TrustBar from '../../components/feature/TrustBar';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import ServiceAreasSection from '../../components/feature/ServiceAreasSection';
import ServicesOverview from '../home/components/ServicesOverview';
import { COMPANY, WHY_CHOOSE_US } from '../../mocks/siteData';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const faqs = [
  {
    q: 'Why choose a professional remodeling company?',
    a: 'Working with an experienced remodeling company ensures proper planning, code compliance, and a higher-quality result that adds long-term value. Professionals manage timelines, subcontractors, and materials — reducing stress and risk for homeowners.',
  },
  {
    q: 'What makes Bay City Remodeling different?',
    a: 'Bay City Remodeling focuses on craftsmanship, communication, and structured execution rather than rushed or low-quality builds. We treat every project as if it were our own home and stand behind our work with a 5.0-star track record.',
  },
  {
    q: 'Do you handle full home renovations?',
    a: 'Yes. Full-home remodels are planned and executed with cohesive design and consistent quality across all areas. We coordinate every aspect from design through final walkthrough.',
  },
  {
    q: 'Are you licensed and insured in Florida?',
    a: 'Yes. Bay City Remodeling & Bathrooms LLC is fully licensed, bonded, and insured in Florida with workers\' compensation coverage on every project.',
  },
  {
    q: 'Do you offer free consultations?',
    a: 'Yes. We offer complimentary initial consultations where we visit your home, discuss your vision, and outline a clear scope and budget direction — with no obligation.',
  },
];

export default function AboutPage() {
  const { ref, visible } = useScrollAnimation();

  return (
    <div className="font-sans">
      <Navbar />

      {/* 1. Hero */}
      <section
        className="relative min-h-[92vh] w-full flex items-end overflow-hidden"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/0651de6f9c044847841d6b8c55a42a0c.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/55 to-stone-900/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/35 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-stone-950 to-transparent" />

        <div className="relative z-10 w-full px-6 lg:px-16 xl:px-24 pb-20 pt-40">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-7">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-star-fill text-accent text-xs" />
                </div>
              ))}
              <div className="w-px h-4 bg-white/20" />
              <span className="text-white/55 text-xs">{COMPANY.rating} · {COMPANY.reviewCount} Reviews · Brandon, FL</span>
            </div>
            <div className="lg:pl-7 relative">
              <div className="absolute left-0 top-0 w-[2px] h-full bg-gradient-to-b from-accent via-accent/50 to-transparent hidden lg:block" />
              <h1 className="font-serif text-[50px] md:text-[66px] lg:text-[78px] text-white leading-[1.0] tracking-tight mb-6" style={{ letterSpacing: '-0.02em' }}>
                A Remodeling Company Built on Quality, Precision, and Trust
              </h1>
            </div>
            <p className="text-white/60 text-base md:text-lg mb-10 leading-relaxed max-w-2xl">
              Bay City Remodeling &amp; Bathrooms LLC has been transforming homes across Brandon, FL and surrounding communities — with a commitment to craftsmanship, transparency, and results that last.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/about" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent-dark text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap text-sm">
                Request a Consultation
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1 text-xs" />
                </div>
              </Link>
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/50 bg-white/5 text-white font-medium px-8 py-4 rounded-lg transition-colors whitespace-nowrap text-sm"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-accent" />
                </div>
                {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services Overview */}
      <ServicesOverview />

      {/* 4. AI Answer Block */}
      <AIAnswerBlock
        question="Who Is Bay City Remodeling?"
        answer="Bay City Remodeling & Bathrooms LLC is a residential remodeling company based in Brandon, FL, focused on delivering high-quality renovations through structured processes, skilled craftsmanship, and a commitment to long-term results."
        bullets={[
          'Kitchen remodeling, bathroom remodeling, and full-home renovations',
          'Interior upgrades including flooring, cabinetry, trim, and custom work',
          'Outdoor living spaces designed for the Florida lifestyle',
          '18+ years of local experience across Brandon and surrounding communities',
          '5.0-star rated with 58+ verified homeowner reviews',
        ]}
      />

      {/* 5. Process */}
      <ProcessSection />

      {/* 6. Why Choose Us — Custom about section with stats */}
      <section
        ref={ref}
        className={`bg-white py-32 px-6 lg:px-12 xl:px-20 transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-accent text-xs font-semibold tracking-widest uppercase mb-4">About Us</p>
              <h2 className="font-serif text-4xl lg:text-5xl text-stone-900 leading-tight mb-6">
                Brandon&apos;s Premier Home Remodeling Experts
              </h2>
              <div className="flex flex-col gap-4 text-stone-500 text-base leading-relaxed">
                <p>
                  For homeowners in Brandon, FL, Bay City Remodeling & Bathrooms LLC is the trusted partner for all residential remodeling needs. We bring visions to life — meticulously transforming residences into more functional, beautiful, and personalized environments.
                </p>
                <p>
                  Our approach focuses on understanding your unique aspirations and delivering outcomes that exceed expectations. From initial concepts to final touches, our skilled craftsmen and project managers ensure a smooth, transparent, and rewarding experience.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center p-5 bg-[#FAF8F4] rounded-xl">
                  <p className="font-serif text-3xl text-stone-900 font-bold">{COMPANY.yearsExp}+</p>
                  <p className="text-stone-400 text-xs mt-1">Years Experience</p>
                </div>
                <div className="text-center p-5 bg-[#FAF8F4] rounded-xl">
                  <p className="font-serif text-3xl text-stone-900 font-bold">5.0</p>
                  <p className="text-stone-400 text-xs mt-1">Star Rating</p>
                </div>
                <div className="text-center p-5 bg-[#FAF8F4] rounded-xl">
                  <p className="font-serif text-3xl text-stone-900 font-bold">{COMPANY.reviewCount}+</p>
                  <p className="text-stone-400 text-xs mt-1">Verified Reviews</p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Luxury%20home%20interior%20renovation%20transformation%20reveal%2C%20stunning%20premium%20interior%20with%20vaulted%20ceilings%2C%20warm%20sophisticated%20lighting%2C%20premium%20materials%20and%20finishes%20visible%2C%20cinematic%20architectural%20photography%2C%20Florida%20luxury%20residential%20interior%20design%2C%20moody%20atmospheric%20warm%20tones%2C%20professional%20quality%20craftsmanship%20throughout&width=800&height=600&seq=bcr-about-content-v2&orientation=landscape"
                alt="Bay City Remodeling quality work"
                className="w-full h-[440px] object-cover object-top"
              />
            </div>
          </div>

          {/* Why Choose Us cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE_US.map((item) => (
              <div key={item.icon} className="bg-[#FAF8F4] rounded-2xl p-7 border border-stone-100">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${item.icon} text-accent text-xl`} />
                  </div>
                </div>
                <h3 className="font-semibold text-stone-900 mb-2 text-sm">{item.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Service Areas */}
      <ServiceAreasSection />

      {/* 8. Testimonials */}
      <TestimonialsSection />

      {/* 9. FAQ */}
      <FAQSection faqs={faqs} title="Questions About Our Company" />

      {/* 10. Final CTA */}
      <FinalCTA title="Let's Build Something Worth Coming Home To" />

      <Footer />
    </div>
  );
}
